"use client";

import type { FC, Ref } from "react";
import {
  AskAIButton,
  Card,
  Flex,
  Text,
  usePubSub,
  type UserPromptRecord,
} from "@huub-web/ui";

import { TemplatesParser } from "../../components";
import type { TemplateCollection } from "../../types";
import { useTemplates } from "../../hooks";

export type PaneTemplateProps = {
  /**
   * The title of the pane
   */
  title?: string;
  /**
   * The description of the pane
   */
  description?: string;
  /**
   * The templates to render in the pane
   */
  templates?: TemplateCollection;
  /**
   * The help prompt to render in the pane
   */
  helpPrompt?: UserPromptRecord;
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

export const PaneTemplate: FC<PaneTemplateProps> = ({
  title,
  description,
  helpPrompt,
  templates,
  ref,
  ...props
}) => {
  const { lockPrompts } = useTemplates();
  const pubSub = usePubSub();

  const handleHelpPrompt = () => {
    if (helpPrompt && !lockPrompts) {
      pubSub.publish("sendPrompt", {
        value: helpPrompt,
      });
    }
  };

  return (
    <Card
      ref={ref}
      variant="panel"
      padding="s"
      gap="m"
      borderRadius="xs"
      {...props}
    >
      {(title || description || helpPrompt) && (
        <Flex direction="column" gap="xs">
          <Flex direction="row" gap="s">
            <Text variant="bodyL" bold grow>
              {title}
            </Text>
            {helpPrompt && (
              <AskAIButton onClick={handleHelpPrompt} disabled={lockPrompts} />
            )}
          </Flex>
          {description && (
            <Text
              variant="bodyS"
              color="currentColor50"
              style={{ maxWidth: "75%" }}
            >
              {description}
            </Text>
          )}
        </Flex>
      )}
      {templates && templates.length > 0 && (
        <TemplatesParser templates={templates} level={1} />
      )}
    </Card>
  );
};
